import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/site/Reveal";
import { CategoryPill } from "@/components/site/Bits";
import { accentFor } from "@/lib/verticals";
import api from "@/lib/api";
import { ArrowRight, FileText } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All", match: () => true },
  { key: "supply", label: "Supply Chain", match: (c) => c.toLowerCase().includes("supply") },
  { key: "marketing", label: "Marketing", match: (c) => c.toLowerCase().includes("market") },
  { key: "it", label: "IT", match: (c) => /\b(it|tech|platform)\b/i.test(c) },
  { key: "learning", label: "Learning", match: (c) => /(learn|educat|capab)/i.test(c) },
];

export default function Insights() {
  useSeo({
    title: "Insights",
    description: "Practical, outcome-led perspectives on redesigning enterprise work with AI across Supply Chain, Marketing, IT and Learning — from ThinkStack.",
    path: "/insights",
  });
  const [articles, setArticles] = useState(null);
  const [params, setParams] = useSearchParams();
  const active = params.get("vertical") || "all";

  useEffect(() => {
    api.get("/insights").then(({ data }) => setArticles(data)).catch(() => setArticles([]));
  }, []);

  const filtered = useMemo(() => {
    if (!articles) return null;
    const f = FILTERS.find((x) => x.key === active) || FILTERS[0];
    return articles.filter((a) => f.match(a.category || ""));
  }, [articles, active]);

  const [featured, ...rest] = filtered || [];

  return (
    <div>
      <section className="border-b border-[#E5E5E5]">
        <div className="mx-auto max-w-7xl px-6 md:px-12 pt-16 pb-12 md:pt-20 md:pb-14">
          <Reveal>
            <p className="overline text-restrained-blue mb-6">Insights & Research</p>
            <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.98] text-deep-navy max-w-4xl">
              Perspectives on redesigning work with AI.
            </h1>
            <p className="mt-8 text-xl text-charcoal/70 max-w-2xl">
              Practical thinking for transformation leaders across Supply Chain, Marketing, IT and Learning. No hype, no unsupported claims.
            </p>
          </Reveal>
          <div className="mt-10 flex flex-wrap gap-2" data-testid="insights-filters">
            {FILTERS.map((f) => (
              <button key={f.key} data-testid={`filter-${f.key}`}
                      onClick={() => setParams(f.key === "all" ? {} : { vertical: f.key })}
                      className={`text-sm px-4 py-2 border transition-colors ${active === f.key ? "bg-deep-navy text-white border-deep-navy" : "border-[#E5E5E5] text-charcoal/70 hover:border-restrained-blue"}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-14 md:py-20">
          {filtered === null ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[0, 1, 2].map((i) => <div key={i} className="h-72 bg-off-white animate-pulse border border-[#E5E5E5]" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="insights-empty" className="border border-dashed border-[#E5E5E5] py-24 text-center">
              <FileText className="mx-auto text-charcoal/30" size={40} />
              <h3 className="font-serif text-2xl text-deep-navy mt-5">No insights in this category yet</h3>
              <p className="text-charcoal/60 mt-3 max-w-md mx-auto">Try another vertical, or bring us a process to discuss.</p>
            </div>
          ) : (
            <>
              {/* Featured */}
              {featured && (
                <Reveal>
                  <Link to={`/insights/${featured.slug}`} data-testid={`insight-featured-${featured.slug}`}
                        className="group grid grid-cols-1 lg:grid-cols-2 gap-0 border border-[#E5E5E5] mb-10 hover:shadow-[0_16px_50px_-16px_rgba(10,25,47,0.25)] transition-all">
                    <div className="aspect-[16/10] lg:aspect-auto overflow-hidden" style={{ borderRight: "1px solid #E5E5E5" }}>
                      <img src={featured.cover_image} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-8 md:p-12 flex flex-col justify-center">
                      <CategoryPill category={featured.category} className="self-start" />
                      <h2 className="font-serif text-3xl md:text-4xl text-deep-navy mt-5 leading-tight">{featured.title}</h2>
                      <p className="mt-4 text-charcoal/70 leading-relaxed">{featured.excerpt}</p>
                      <div className="mt-6 flex items-center gap-3 text-sm text-charcoal/50">
                        <span>{featured.author}</span><span>·</span><span>{featured.read_time}</span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              )}

              {/* Grid */}
              {rest.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="insights-list">
                  {rest.map((a, i) => {
                    const accent = accentFor(a.category);
                    return (
                      <Reveal key={a.id} delay={i * 0.05}>
                        <Link to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`}
                              className="group block bg-white border border-[#E5E5E5] h-full transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_-12px_rgba(10,25,47,0.22)]">
                          <div className="aspect-[16/10] overflow-hidden border-b border-[#E5E5E5] relative">
                            <span className="absolute top-0 left-0 z-10 h-1.5 w-full" style={{ backgroundColor: accent.color }} />
                            <img src={a.cover_image} alt="" loading="lazy"
                                 className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                          </div>
                          <div className="p-7">
                            <CategoryPill category={a.category} className="self-start" />
                            <h3 className="font-serif text-2xl text-deep-navy mt-4 leading-snug">{a.title}</h3>
                            <p className="mt-3 text-charcoal/70 text-sm leading-relaxed line-clamp-3">{a.excerpt}</p>
                            <div className="mt-5 flex items-center justify-between text-xs text-charcoal/50">
                              <span>{a.read_time}</span>
                              <span className="inline-flex items-center gap-1 group-hover:gap-2 transition-all" style={{ color: accent.color }}>Read <ArrowRight size={13} /></span>
                            </div>
                          </div>
                        </Link>
                      </Reveal>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
